import React from "react";
import { api } from "@/lib/backend";
import { type SigninInput } from "@/lib/backend/api";
import { BetterAuthError } from "@/lib/backend/utils/error";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { authClient } from "@/utils/auth";
import { SessionContext } from "./context";

// File: src/lib/providers/global/session/index.tsx

/**
 * SessionProvider component
 *
 * This component utilizes the Better Auth library (https://github.com/better-auth/better-auth) to
 * manage authentication state and actions.
 *
 * The Better Auth library provides:
 *
 * - Session management through atoms and signals
 * - A configurable fetch instance for API calls
 * - Plugin system for extending functionality
 *
 * In this implementation:
 *
 * - We use oldAuthClient.useSession() to access the current session state
 * - The signin mutation is handled locally, using api.signUpAdmin
 * - After successful signin, we notify the auth store to update the session using
 *   oldAuthClient.$store.notify("$sessionSignal")
 * - The SessionContext provides session data, signin, and signout functions to child components
 *
 * @see https://github.com/better-auth/better-auth/blob/main/packages/better-auth/src/client/config.ts
 */

async function signinWithFallback(vars: SigninInput) {
  try {
    return await api.api.signinEmployee(vars);
  } catch (error) {
    const parsed = BetterAuthError.safeParse(error);
    if (
      parsed.success &&
      parsed.data.code === "USER_WITH_NOT_AUTHORIZED_TO_ACCESS_EMPLOYEE_SIGNIN"
    ) {
      return await api.api.signinAdmin(vars);
    }
    throw error;
  }
}

export function SessionProvider({ children }: React.PropsWithChildren) {
  const session = authClient.useSession();

  const signin = useExtractedMutation({
    mutationKey: ["signin"],
    mutationFn: signinWithFallback,
    onSuccess: () => {
      authClient.$store.notify("$sessionSignal");
    },
  });

  return (
    <SessionContext.Provider
      value={{
        session,
        signin: signin.mutateAsync,
        signout: authClient.signOut,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}
